import { useCallback, useEffect, useState } from "react";
import { getItineraryRequestCount } from "../services/itinerary";

/**
 * How many itinerary requests are waiting in RosterPilot's queue — the number behind the Admin
 * nav badge.
 *
 * Two places need it and they must agree: the badge in `DashboardNav`, and the queue itself, which
 * drops the count the moment a request is fulfilled or declined. A plain per-component hook would
 * fetch twice and leave the badge stale after a decision, so the value lives in a tiny
 * module-level store both subscribe to — the same shape as `useDuplicateCount`, which this
 * deliberately mirrors rather than inventing a second badge idiom.
 *
 * <b>`enabled` is not an optimization.</b> The count endpoint is `system_admin` only, so calling it
 * as a coach is a guaranteed 403 on every page load. The Admin nav item is system-admin-only too,
 * so a disabled hook is a badge that could never have rendered anyway.
 */

let cachedCount = 0;
let inFlight: Promise<void> | null = null;
/** A refresh asked for while a read was already out. See `fetchCount`. */
let refetchQueued = false;
const listeners = new Set<(count: number) => void>();

const publish = (count: number) => {
  cachedCount = count;
  listeners.forEach((listener) => listener(count));
};

const fetchCount = (): Promise<void> => {
  // Collapse concurrent reads into one request — but only the READ. A refresh asked for while one
  // is in flight is an INVALIDATION, and it needs data the outstanding request cannot contain:
  // decline request A, then decline B a moment later, and plain collapsing publishes the count the
  // server computed before B was declined. The badge then sits one too high, contradicting the
  // queue list beside it, until something remounts. So a queued refresh re-runs once on settle.
  if (inFlight) {
    refetchQueued = true;
    return inFlight;
  }

  inFlight = getItineraryRequestCount()
    .then((result) => publish(result.count))
    .catch(() => {
      // A failed badge fetch is not worth surfacing — the badge keeps its last known value rather
      // than pushing an error into the nav chrome.
    })
    .finally(() => {
      inFlight = null;
      if (refetchQueued) {
        refetchQueued = false;
        void fetchCount();
      }
    });

  return inFlight;
};

/** Test-only reset so the module store does not leak between test cases. */
export const __resetItineraryRequestCountStore = () => {
  cachedCount = 0;
  inFlight = null;
  refetchQueued = false;
  listeners.clear();
};

export const useItineraryRequestCount = (enabled = true) => {
  const [count, setCount] = useState(enabled ? cachedCount : 0);

  useEffect(() => {
    if (!enabled) return;
    // Seed from the cache HERE as well as at mount: another consumer may have populated it while
    // this one was disabled, and `enabled` flips false -> true during auth bootstrap.
    setCount(cachedCount);
    listeners.add(setCount);
    void fetchCount();
    return () => {
      listeners.delete(setCount);
    };
  }, [enabled]);

  const refresh = useCallback(() => {
    if (enabled) void fetchCount();
  }, [enabled]);

  return { count, refresh };
};
